import {Button} from '@wandb/weave/components';
import React from 'react';

import {ChartAggOption} from '../../../../generated/graphql';
import {calculateIsSettingEnabled} from '../../../../state/views/workspaceSettings/settingOverrideUtils';
import {ClearSettingsRows} from '../components/ClearSettingsRow';
import {ResetSettingButton} from '../components/ResetSettingButton';
import {SettingIndentedRow} from '../components/SettingIndentedRow';
import {SettingLabel} from '../components/SettingLabel';
import {SettingToggleRow} from '../components/SettingToggleRow';
import {useClearSettingRows} from '../hooks/useClearSettingRows';
import {useLinePlotSettingsContext} from './LinePlotSettingsContextProvider';
import {useGroupRunsV2QueryingEnabled} from './useGroupRunsV2QueryingEnabled';
import {useLinePlotSettingsWithDefaults} from './utils/getLinePlotSettings';

export const LinePlotGroupingSetting = () => {
  const {displayLinePlotSettings} = useLinePlotSettingsContext();
  const {useRunsTableGroupingInPanels} = useLinePlotSettingsWithDefaults(
    displayLinePlotSettings
  );

  return (
    <div className="flex flex-col gap-8">
      <RunsTableGroupingSetting />
      {useRunsTableGroupingInPanels && (
        <SettingIndentedRow className="gap-8 pl-14">
          <LatestRunPerGroupSetting />
          <StabilizeRunGroupsSetting />
          <GroupAggregationSetting />
        </SettingIndentedRow>
      )}
    </div>
  );
};

export const RunsTableGroupingSetting = () => {
  const {
    clearSetting,
    displayLinePlotSettings,
    linePlotSettings,
    settingLevel,
    settingOverrideCounts,
    trackLinePlotSetting,
    updateLinePlotSettings,
  } = useLinePlotSettingsContext();
  const {useRunsTableGroupingInPanels} = useLinePlotSettingsWithDefaults(
    displayLinePlotSettings
  );

  const clearSettingRows = useClearSettingRows({
    numOverrides: settingOverrideCounts,
    settingKey: 'useRunsTableGroupingInPanels',
    settingLevel,
    clearSetting,
    emptySettings: {useRunsTableGroupingInPanels: undefined},
  });

  return (
    <div>
      <SettingToggleRow
        clearSetting={clearSetting}
        displayValue={useRunsTableGroupingInPanels}
        emptySettings={{useRunsTableGroupingInPanels: undefined}}
        label="Use grouping from the runs table in charts"
        onCheckedChange={(newValue: boolean) => {
          updateLinePlotSettings({
            useRunsTableGroupingInPanels: newValue,
          });
          trackLinePlotSetting(
            'useRunsTableGroupingInPanels',
            newValue.toString()
          );
        }}
        settingKey="useRunsTableGroupingInPanels"
        settingLevel={settingLevel}
        value={linePlotSettings?.useRunsTableGroupingInPanels}
      />
      <ClearSettingsRows rows={clearSettingRows} />
    </div>
  );
};

export const LatestRunPerGroupSetting = () => {
  const {
    displayLinePlotSettings,
    settingLevel,
    trackLinePlotSetting,
    updateLinePlotSettings,
  } = useLinePlotSettingsContext();
  if (settingLevel !== 'workspace') {
    return null;
  }

  const isLatestRunPerGroupEnabled = Number.isFinite(
    displayLinePlotSettings?.groupingDepth
  );

  return (
    <SettingToggleRow
      displayValue={isLatestRunPerGroupEnabled}
      label="Use only latest run per group"
      onCheckedChange={(newValue: boolean) => {
        const groupingDepth = newValue ? 1 : undefined;
        updateLinePlotSettings({groupingDepth});
        trackLinePlotSetting('groupingDepth', groupingDepth?.toString());
      }}
      value={isLatestRunPerGroupEnabled}
    />
  );
};

export const STABILIZE_RUN_GROUPS_INFO =
  "Keeps each group's runs the same when you hide or unhide other groups. " +
  'Has no effect when grouping by more than 4 keys or by keys that are slow ' +
  'to group, such as summary metrics.';

export const StabilizeRunGroupsSetting = () => {
  const {
    displayLinePlotSettings,
    settingLevel,
    trackLinePlotSetting,
    updateLinePlotSettings,
  } = useLinePlotSettingsContext();
  const {stabilizeRunGroups} = useLinePlotSettingsWithDefaults(
    displayLinePlotSettings
  );
  const isGroupRunsV2Enabled = useGroupRunsV2QueryingEnabled();
  // Backend only honors stabilize on the v2 path, which this entity gate opens.
  if (settingLevel !== 'workspace' || !isGroupRunsV2Enabled) {
    return null;
  }

  return (
    <SettingToggleRow
      displayValue={stabilizeRunGroups}
      info={STABILIZE_RUN_GROUPS_INFO}
      label="Stabilize run groups"
      onCheckedChange={(newValue: boolean) => {
        updateLinePlotSettings({stabilizeRunGroups: newValue});
        trackLinePlotSetting('stabilizeRunGroups', newValue.toString());
      }}
      value={stabilizeRunGroups}
    />
  );
};

const GROUP_AGGREGATION_OPTIONS: {
  label: string;
  value: ChartAggOption;
}[] = [
  {label: 'Mean', value: ChartAggOption.Mean},
  {label: 'Min', value: ChartAggOption.Min},
  {label: 'Max', value: ChartAggOption.Max},
];

const GroupAggregationSetting = () => {
  const {
    clearSetting,
    displayLinePlotSettings,
    linePlotSettings,
    settingLevel,
    settingOverrideCounts,
    trackLinePlotSetting,
    updateLinePlotSettings,
  } = useLinePlotSettingsContext();
  const {groupAgg} = useLinePlotSettingsWithDefaults(displayLinePlotSettings);
  const isGroupAggregationDisabled = Number.isFinite(
    displayLinePlotSettings?.groupingDepth
  );

  const clearSettingRows = useClearSettingRows({
    numOverrides: settingOverrideCounts,
    settingKey: 'groupAgg',
    settingLevel,
    clearSetting,
    emptySettings: {groupAgg: undefined},
  });

  return (
    <div>
      <div className="flex items-center justify-between">
        <SettingLabel text="Aggregation" className="w-[87px]" />
        <div className="flex items-center gap-8">
          <div className="flex items-center gap-2">
            {GROUP_AGGREGATION_OPTIONS.map(option => (
              <Button
                key={option.value}
                active={groupAgg === option.value}
                disabled={isGroupAggregationDisabled}
                variant="secondary"
                onClick={() => {
                  updateLinePlotSettings({groupAgg: option.value});
                  trackLinePlotSetting('groupAgg', option.value);
                }}>
                {option.label}
              </Button>
            ))}
          </div>
          <ResetSettingButton
            isEnabled={
              !isGroupAggregationDisabled &&
              calculateIsSettingEnabled('groupAgg', linePlotSettings)
            }
            settingLevel={settingLevel}
            onButtonClick={() => {
              clearSetting(settingLevel, 'groupAgg', {groupAgg: undefined});
            }}
          />
        </div>
      </div>
      <ClearSettingsRows rows={clearSettingRows} />
    </div>
  );
};
